import { FC, useCallback } from 'react';
import { Form, useFormikContext } from 'formik';
import { useFragment } from 'react-relay';
import graphql from 'babel-plugin-relay/macro';

import PreviewIcon from '@mui/icons-material/Preview';
import SaveIcon from '@mui/icons-material/Save';
import DeleteIcon from '@mui/icons-material/Delete';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import Button from '@mui/material/Button';
import LoadingButton from '@mui/lab/LoadingButton';
import TableContainer from '@mui/material/TableContainer';
import Table from '@mui/material/Table';
import TableHead from '@mui/material/TableHead';
import TableBody from '@mui/material/TableBody';
import TableRow from '@mui/material/TableRow';
import Box from '@mui/material/Box';
import TableCell from 'components/TableCell';
import TextField from 'components/TextField';
import PriceInput from 'components/PriceInput';
import RoomNameWithCapacity from 'components/RoomNameWithCapacity';
import CateringOptionName from 'components/CateringOptionName';
import ConfirmableLoadingButton from 'components/ConfirmableLoadingButton';
import RoomRatesTableRateEditorPriceBreakdown from 'components/RoomRatesTableRateEditorPriceBreakdown';

import { useAvoidSuspense } from 'lib';
import { useDialog } from 'components/Dialog';
import { FormFields } from 'components/RoomRatesTableRateEditor';

import Query, {
  RoomRatesTableRateEditorFormQuery,
} from '__generated__/RoomRatesTableRateEditorFormQuery.graphql';
import { RoomRatesTableRateEditorForm_rateByRoom$key } from '__generated__/RoomRatesTableRateEditorForm_rateByRoom.graphql';
import { RoomRatesTableRateEditorForm_cateringOption$key } from '__generated__/RoomRatesTableRateEditorForm_cateringOption.graphql';
import { RoomRatesTableRateEditorForm_package$key } from '__generated__/RoomRatesTableRateEditorForm_package.graphql';
import { RoomRatesTableRateEditorForm_packageDate$key } from '__generated__/RoomRatesTableRateEditorForm_packageDate.graphql';
import { RoomRatesTableRateEditorForm_data$key } from '__generated__/RoomRatesTableRateEditorForm_data.graphql';

type Props = {
  data: RoomRatesTableRateEditorForm_data$key;
  rateByRoom: RoomRatesTableRateEditorForm_rateByRoom$key;
  cateringOption: RoomRatesTableRateEditorForm_cateringOption$key;
  package: RoomRatesTableRateEditorForm_package$key;
  packageDate: RoomRatesTableRateEditorForm_packageDate$key;
  storing: boolean;
  deleting: boolean;
  onDelete: () => void;
};

const RoomRatesTableRateEditorForm: FC<Props> = props => {
  const { storing, deleting, onDelete, ...rest } = props;
  const { close } = useDialog();
  const { values, isValid, setTouched } = useFormikContext<FormFields>();

  const [{ previewRoomRate }, preview] = useAvoidSuspense<
    RoomRatesTableRateEditorFormQuery,
    RoomRatesTableRateEditorForm_data$key
  >(
    graphql`
      fragment RoomRatesTableRateEditorForm_data on Query
      @refetchable(queryName: "RoomRatesTableRateEditorFormQuery")
      @argumentDefinitions(preview: { type: "Boolean", defaultValue: false }) {
        previewRoomRate(packageDateId: $packageDateId, input: $input)
          @include(if: $preview) {
          ...RoomRatesTableRateEditorPriceBreakdown_roomRate
        }
      }
    `,
    props.data,
    Query
  );

  const { room, roomRate } = useFragment(
    graphql`
      fragment RoomRatesTableRateEditorForm_rateByRoom on RateByRoom {
        room {
          ...RoomNameWithCapacity_room
        }

        roomRate {
          ...RoomRatesTableRateEditorPriceBreakdown_roomRate
        }
      }
    `,
    rest.rateByRoom
  );

  const cateringOption = useFragment(
    graphql`
      fragment RoomRatesTableRateEditorForm_cateringOption on CateringOption {
        id

        ...CateringOptionName_cateringOption
      }
    `,
    rest.cateringOption
  );

  const { transferPrice } = useFragment(
    graphql`
      fragment RoomRatesTableRateEditorForm_package on Package {
        transferPrice
      }
    `,
    rest.package
  );

  const { id, start, end, flight } = useFragment(
    graphql`
      fragment RoomRatesTableRateEditorForm_packageDate on PackageDate {
        id
        start
        end

        flight {
          priceLive
        }
      }
    `,
    rest.packageDate
  );

  const handlePreviewClick = useCallback(() => {
    setTouched({
      roomPriceAdult: true,
      roomPriceChild: true,
    });

    if (isValid) {
      preview.refetch({
        packageDateId: id,
        input: {
          roomPriceAdult: Number(values.roomPriceAdult),
          roomPriceChild: Number(values.roomPriceChild),
        },
        preview: true,
      });
    }
  }, [
    id,
    isValid,
    preview,
    setTouched,
    values.roomPriceAdult,
    values.roomPriceChild,
  ]);

  return (
    <Form>
      <DialogTitle sx={{ display: 'flex' }}>
        <Box sx={{ flexGrow: 1 }}>
          Kelionės kaina ({start} - {end})
        </Box>
      </DialogTitle>

      <DialogContent dividers sx={{ maxHeight: 'calc(100vh - 40vh)' }}>
        <DialogContentText>
          <RoomNameWithCapacity room={room} />.{' '}
          <CateringOptionName cateringOption={cateringOption} />
        </DialogContentText>

        <TextField
          fullWidth
          name="priceNett"
          label="Nett kaina asmeniui"
          sx={{ mt: 2 }}
        />

        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell />

                <TableCell center sx={{ fontWeight: 'bold' }}>
                  Suaugęs
                </TableCell>

                <TableCell center sx={{ fontWeight: 'bold' }}>
                  Vaikas
                </TableCell>
              </TableRow>
            </TableHead>

            <TableBody>
              <TableRow>
                <TableCell>Nakvynė</TableCell>

                <TableCell center>
                  <TextField
                    autoFocus
                    type="number"
                    component={PriceInput}
                    name="roomPriceAdult"
                    sx={{
                      width: '70px',
                    }}
                  />
                </TableCell>

                <TableCell center>
                  <TextField
                    type="number"
                    component={PriceInput}
                    name="roomPriceChild"
                    sx={{
                      width: '70px',
                    }}
                  />
                </TableCell>
              </TableRow>

              {(roomRate || previewRoomRate) && (
                <>
                  {flight && (
                    <TableRow>
                      <TableCell>Skrydis</TableCell>
                      <TableCell center>€{flight.priceLive}</TableCell>
                      <TableCell center>€{flight.priceLive}</TableCell>
                    </TableRow>
                  )}

                  {transferPrice && transferPrice > 0 ? (
                    <TableRow>
                      <TableCell>Pervežimas</TableCell>
                      <TableCell center>€{transferPrice}</TableCell>
                      <TableCell center>€{transferPrice}</TableCell>
                    </TableRow>
                  ) : null}

                  <RoomRatesTableRateEditorPriceBreakdown
                    roomRate={previewRoomRate || roomRate}
                  />
                </>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions>
        {roomRate && (
          <ConfirmableLoadingButton
            loading={deleting}
            onConfirm={onDelete}
            color="error"
            variant="outlined"
            startIcon={<DeleteIcon />}>
            Trinti
          </ConfirmableLoadingButton>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Button onClick={close} variant="text">
          Uždaryti
        </Button>

        <LoadingButton
          loading={preview.loading}
          variant="outlined"
          startIcon={<PreviewIcon />}
          onClick={handlePreviewClick}>
          Peržiūrėti
        </LoadingButton>

        <LoadingButton
          loading={storing}
          type="submit"
          variant="contained"
          startIcon={<SaveIcon />}>
          Saugoti
        </LoadingButton>
      </DialogActions>
    </Form>
  );
};

export default RoomRatesTableRateEditorForm;
